<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import dayjs from 'dayjs';
    import { computed, ref, watch } from 'vue';
    import EchoButton from '../../EchoButton.vue';
    import IconButton from '../../IconButton.vue';
    import SelectInput from '../../SelectInput.vue';
    import DatePickerInput from '../../DatePickerInput.vue';
    import PasswordInput from '../../PasswordInput.vue';
    import { useSharedDashboardStore } from '@/js/stores/sharedDashboard';
    import { IDashboardLink } from '@/js/services/api/shareDashboard/types';
    import { useToastStore } from '@/js/stores/snackbar';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import ToggleSwitch from '../../ToggleSwitch.vue';
    import CopyTextInput from '../../CopyTextInput.vue';
    import { TextInputWidth } from '../../TextInput.vue';
    import InputLabel from '../../InputLabel.vue';
    import FormGroup from '../../FormGroup.vue';
    import EchoLabel from '../../EchoLabel.vue';
    import { dateToISO } from '@/js/utils/dateUtils';
    import { PlusIcon, SettingsIcon, TrashIcon } from '@lucide/vue';

    const props = defineProps<{
        dashboardId: string;
    }>();

    const sharedDashboard = useSharedDashboardStore();
    const toast = useToastStore();
    const confirm = useConfirmationDialog();

    type ViewType = 'list' | 'create' | 'edit';
    const view = ref<ViewType>('list');
    const editingLink = ref<IDashboardLink | null>(null);

    // Form fields
    const accessType = ref<string>('anyone-with-link');
    const expirationDate = ref<string>('');
    const password = ref<string>('');
    const showOnlyActive = ref<boolean>(false);
    const isCreating = ref<boolean>(false);

    const initialAccessType = ref<string>('');
    const initialExpirationDate = ref<string>('');
    const initialPassword = ref<string>('');

    const sharedDashboardLinks = computed(() => sharedDashboard.sharedDashboardLinks ?? []);

    const isLinkExpired = (link: IDashboardLink): boolean => {
        if (!link.expirationDate) return false;
        return dayjs(link.expirationDate).isBefore(dayjs()); // exact instant
    };

    const filteredLinks = computed(() => {
        if (!showOnlyActive.value) {
            return sharedDashboardLinks.value;
        }
        return sharedDashboardLinks.value.filter((link) => !isLinkExpired(link));
    });

    const requiresPassword = computed(() => accessType.value === 'password-required');

    const isDateTodayOrLater = (dateStr: string): boolean => {
        const today = dayjs().startOf('day');
        return !dayjs(dateStr).isBefore(today, 'day');
    };

    const snapshotFormState = () => {
        initialAccessType.value = accessType.value;
        initialExpirationDate.value = expirationDate.value;
        initialPassword.value = password.value;
    };

    const openCreateView = () => {
        view.value = 'create';
        accessType.value = 'anyone-with-link';
        expirationDate.value = dayjs().add(1, 'month').format('YYYY-MM-DD');
        password.value = '';
        snapshotFormState();
    };

    const openEditView = (link: IDashboardLink) => {
        view.value = 'edit';
        editingLink.value = link;
        accessType.value = link.isPasswordProtected ? 'password-required' : 'anyone-with-link';
        expirationDate.value = dateToISO(link.expirationDate);
        password.value = link.password ?? '';
        snapshotFormState();
    };

    const backToList = () => {
        view.value = 'list';
        editingLink.value = null;
        accessType.value = 'anyone-with-link';
        expirationDate.value = '';
        password.value = '';
    };

    const createNewLink = async () => {
        if (!expirationDate.value) {
            toast.addToast('error', __('share-dashboard-link-date-required'));
            return;
        }

        if (!isDateTodayOrLater(expirationDate.value)) {
            toast.addToast('error', __('share-dashboard-link-date-must-be-future'));
            return;
        }

        if (requiresPassword.value && !password.value) {
            toast.addToast('error', __('share-dashboard-link-password-required'));
            return;
        }

        isCreating.value = true;

        try {
            await sharedDashboard.createNewSharedLink(
                props.dashboardId,
                password.value || undefined,
                expirationDate.value
            );

            await sharedDashboard.getSharedDashboardLinksByDashboard(props.dashboardId);

            toast.addToast('success', __('share-dashboard-link-created'));
            backToList();
        } catch (error) {
            console.error('Error creating shared link:', error);
            toast.addToast('error', __('share-dashboard-link-create-error'));
        } finally {
            isCreating.value = false;
        }
    };

    const updateLink = async () => {
        if (!editingLink.value) return;

        if (!expirationDate.value) {
            toast.addToast('error', __('share-dashboard-link-date-required'));
            return;
        }

        if (!isDateTodayOrLater(expirationDate.value)) {
            toast.addToast('error', __('share-dashboard-link-date-must-be-future'));
            return;
        }

        if (requiresPassword.value && !password.value) {
            toast.addToast('error', __('share-dashboard-link-password-required'));
            return;
        }

        isCreating.value = true;

        try {
            await sharedDashboard.updateSharedDashboardLink(
                props.dashboardId,
                editingLink.value.id,
                password.value || undefined,
                expirationDate.value
            );

            await sharedDashboard.getSharedDashboardLinksByDashboard(props.dashboardId);

            toast.addToast('success', __('share-dashboard-link-updated'));
            backToList();
        } catch (error) {
            console.error('Error updating link:', error);
            toast.addToast('error', __('share-dashboard-link-update-error'));
        } finally {
            isCreating.value = false;
        }
    };

    const deleteLink = async (linkId: string) => {
        const confirmed = await confirm.openDialog({
            title: __('share-dashboard-link-delete-confirm-title'),
            message: __('share-dashboard-link-delete-confirm-description'),
            confirmText: __('delete'),
        });

        if (!confirmed) return;

        try {
            await sharedDashboard.removeSharedDashboardLink(props.dashboardId, linkId);
            toast.addToast('success', __('share-dashboard-link-deleted'));
        } catch (error) {
            console.error('Error deleting link:', error);
            toast.addToast('error', __('share-dashboard-link-delete-error'));
        }
    };

    // Load links on mount and determine initial view
    watch(
        () => props.dashboardId,
        async (newId) => {
            if (newId) {
                await sharedDashboard.getSharedDashboardLinksByDashboard(newId);
                // If no links exist, show create form directly
                if (sharedDashboardLinks.value.length === 0) {
                    openCreateView();
                }
            }
        },
        { immediate: true }
    );

    // Watch for links changes - if we're on create view and links become empty again, stay on create
    watch(sharedDashboardLinks, (newLinks) => {
        if (newLinks.length === 0 && view.value === 'list') {
            openCreateView();
        }
    });

    const hasUnsavedChanges = computed(() => {
        if (view.value === 'list') {
            return false;
        }
        const normalizedPassword = password.value ?? '';
        const normalizedInitialPassword = initialPassword.value ?? '';
        return (
            accessType.value !== initialAccessType.value ||
            expirationDate.value !== initialExpirationDate.value ||
            normalizedPassword !== normalizedInitialPassword
        );
    });

    defineExpose({ hasUnsavedChanges });
</script>

<template>
    <!-- List View -->
    <div v-if="view === 'list'" class="flex-grow flex flex-col">
        <div class="mb-6">
            <h2 class="heading-md-light">
                {{ __('manage-previously-created-links') }}
            </h2>
            <p class="text-body">{{ __('manage-links-description') }}</p>
        </div>

        <div class="flex justify-end mb-4">
            <ToggleSwitch v-model="showOnlyActive" :label="__('only-active')" />
        </div>

        <div class="lg:h-0 flex-grow flex flex-col">
            <div
                class="overflow-auto custom-scrollbar sm:border sm:border-gray-light sm:rounded-sm"
            >
                <div class="sm:px-6">
                    <div
                        v-for="link in filteredLinks"
                        :key="link.id"
                        class="py-6 not-first:border-t border-gray-light"
                    >
                        <div class="flex items-start justify-between mb-2">
                            <div class="flex w-full items-center gap-4">
                                <h4 class="text-body-bold">
                                    {{ dateToISO(link.expirationDate) }}
                                </h4>
                                <div>
                                    <EchoLabel :variant="isLinkExpired(link) ? 'light' : 'solid'">
                                        {{
                                            isLinkExpired(link)
                                                ? __('link-expired')
                                                : __('link-active')
                                        }}
                                    </EchoLabel>
                                </div>
                            </div>
                        </div>

                        <div class="flex">
                            <CopyTextInput v-model="link.uri" :width="TextInputWidth.Full" />

                            <div class="flex items-center ml-2">
                                <IconButton :tooltip="__('settings')" @click="openEditView(link)">
                                    <SettingsIcon class="size-full" />
                                </IconButton>
                                <IconButton :tooltip="__('delete')" @click="deleteLink(link.id)">
                                    <TrashIcon class="size-full" />
                                </IconButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="pt-5 flex justify-end">
                <EchoButton variant="primary" @click="openCreateView">
                    {{ __('add-link') }}
                    <template #icon-right><PlusIcon /></template>
                </EchoButton>
            </div>
        </div>
    </div>

    <!-- Create/Edit Form View -->
    <div v-else class="flex-grow flex flex-col">
        <div class="mb-6">
            <h2 class="heading-md-light">
                {{ view === 'create' ? __('create-new-link') : __('edit-link') }}
            </h2>
            <p class="text-body">
                {{ __('share-with-link-description') }}
            </p>
        </div>

        <FormGroup>
            <div>
                <InputLabel for="accessType">
                    {{ __('access') }}
                </InputLabel>
                <SelectInput id="accessType" v-model="accessType" size="full">
                    <option value="anyone-with-link">
                        {{ __('anyone-with-link') }}
                    </option>
                    <option value="password-required">
                        {{ __('password-required') }}
                    </option>
                </SelectInput>
            </div>

            <div class="-mb-5">
                <InputLabel for="expirationDate">
                    {{ __('expiration-date') }}
                </InputLabel>
                <DatePickerInput
                    v-model="expirationDate"
                    :placeholder="__('set-expiration-date')"
                />
            </div>

            <div v-if="requiresPassword" class="-mb-5">
                <InputLabel for="password">
                    {{ __('password') }}
                </InputLabel>
                <PasswordInput
                    v-model="password"
                    :placeholder="__('password-required-placeholder')"
                    autocomplete="new-password"
                />
            </div>
        </FormGroup>

        <div class="flex justify-end gap-2 pt-10">
            <EchoButton
                v-if="sharedDashboardLinks.length > 0"
                variant="secondary"
                @click="backToList"
            >
                {{ __('cancel') }}
            </EchoButton>
            <EchoButton
                variant="primary"
                :loading="isCreating"
                :disabled="!expirationDate"
                @click="view === 'create' ? createNewLink() : updateLink()"
            >
                {{ view === 'create' ? __('create-link') : __('save') }}
            </EchoButton>
        </div>
    </div>
</template>
